import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import PhotoUploader from "./PhotoUploader";
import {
  ArrowLeft, Check, ChevronDown, ChevronUp, Sparkles,
  KeyRound, LogIn, LogOut, ShieldAlert, Info, RotateCcw,
} from "lucide-react";
import { toast } from "sonner";

/* ---------------- step definitions -------------------------------------- */
// Types: check | count | yesno | text | photo_report | info

const CHECKIN_STEPS = [
  { key: "find_boat", type: "check", no: "Finn båten du har bestilt", en: "Locate your booked boat" },
  {
    key: "key_code_box", type: "check",
    no: "Nøkkelen til båten ligger i en kodeboks til venstre for inngangsdøren i båten. Koden får du på egen melding.",
    en: "The key is in a code box on the left of the boat entrance. You receive the code in a separate message.",
    hint_icon: "key",
  },
  { key: "damage_report", type: "photo_report",
    no: "Sjekk for skader – rapporter alt du mener bør bemerkes før du starter turen (bilde valgfritt)",
    en: "Check for damage – report anything you think should be noted before starting (photo optional)",
    optional_note: true,
  },
  { key: "rope_count", type: "count",
    no: "Hvor mange tau henger langs railen på båten?",
    en: "How many mooring lines are hanging along the rail?" },
  { key: "fender_count", type: "count",
    no: "Hvor mange vanlige fendere henger på båten? (ikke slusefenderen)",
    en: "How many regular fenders are on the boat? (excluding the lock fender)" },
  { key: "lock_fender", type: "yesno",
    no: "Henger det slusefender på båten?",
    en: "Is the lock fender on board?" },
  { key: "boat_clean", type: "yesno",
    no: "Opplever du båten som rengjort og klar?",
    en: "Does the boat feel clean and ready?" },
  { key: "power_connected", type: "yesno",
    no: "Er båten tilkoblet strøm?",
    en: "Is the boat connected to shore power?" },
  { key: "other_notes", type: "text",
    no: "Andre mangler eller informasjon du vil dele med Canal Boats?",
    en: "Other issues or information you would like to share?",
    optional_note: true },
  { key: "have_fun", type: "info",
    no: "Ha en fortreffelig tur – nyt tiden på vannet.",
    en: "Have a fantastic trip – enjoy the time on the water.",
    tone: "celebrate" },
];

const CHECKOUT_STEPS = [
  { key: "all_switches_off", type: "check",
    no: "Skru av alle brytere på hovedpanelet",
    en: "Turn off all switches on the main panel" },
  { key: "driveunit_ignition_off", type: "check",
    no: "Skru av driveunit (motor) og tenningen på båten",
    en: "Turn off the drive unit (motor) and the ignition" },
  { key: "damage_report", type: "photo_report",
    no: "Er det noe du vil rapportere som har skjedd underveis? Bruk gjerne bilder.",
    en: "Anything you'd like to report from the trip? Photos welcome.",
    optional_note: true },
  { key: "rope_count", type: "count",
    no: "Hvor mange tau henger på båten? Skriv antall.",
    en: "How many mooring lines are on the boat? Enter the number." },
  { key: "fender_count", type: "count",
    no: "Hvor mange vanlige fendere henger på båten? Skriv antall.",
    en: "How many regular fenders are on the boat? Enter the number." },
  { key: "lock_fender", type: "yesno",
    no: "Henger det slusefender på båten?",
    en: "Is the lock fender on the boat?" },
  { key: "lock_key_in_box", type: "check",
    no: "Lås døren og legg nøkkelen i kodeboksen",
    en: "Lock the door and place the key back in the code box",
    hint_icon: "key" },
  { key: "hatch_and_belongings", type: "check",
    no: "Se til at lem er sikret, at ingen personlige gjenstander er glemt og forlat båten sikret.",
    en: "Ensure the hatch is secured, no personal belongings are left behind, and the boat is safely locked up." },
  { key: "thanks", type: "info",
    no: "Vi håper du har hatt en nydelig tur. Tusen takk for at du valgte Canal Boats Telemark.",
    en: "We hope you had a wonderful trip. Thank you for choosing Canal Boats Telemark.",
    tone: "celebrate" },
];

const STORAGE_KEY = "cbt:self_checks_v1";
function loadDraft(bookingId, kind) {
  if (!bookingId) return { items: {}, cursor: 0, started: false };
  try {
    const all = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
    return (all[bookingId] || {})[kind] || { items: {}, cursor: 0, started: false };
  } catch {
    return { items: {}, cursor: 0, started: false };
  }
}
function saveDraft(bookingId, kind, state) {
  if (!bookingId) return;
  try {
    const all = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
    all[bookingId] = { ...(all[bookingId] || {}), [kind]: state };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
  } catch { /* noop */ }
}
function clearDraft(bookingId, kind) {
  try {
    const all = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
    if (all[bookingId]) {
      delete all[bookingId][kind];
      localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
    }
  } catch { /* noop */ }
}

/* ---------------- helpers ---------------------------------------------- */
function stepAnswered(step, answer) {
  if (!step) return false;
  if (step.type === "info") return true;
  if (step.optional_note) return true;
  const a = answer || {};
  switch (step.type) {
    case "check": return a.value === true;
    case "yesno": return a.value === true || a.value === false;
    case "count": return typeof a.value === "number" && a.value >= 0;
    case "text": return typeof a.text === "string" && a.text.trim().length > 0;
    case "photo_report": return true; // optional by design
    default: return false;
  }
}

/* ---------------- component --------------------------------------------- */
export default function SelfCheckWizard({
  lang = "no", bookingId, kind, enabled, alreadySubmitted, onSubmitted,
}) {
  const steps = kind === "checkin" ? CHECKIN_STEPS : CHECKOUT_STEPS;
  const [state, setState] = useState({ items: {}, cursor: 0, started: false });
  const [expanded, setExpanded] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setState(loadDraft(bookingId, kind));
  }, [bookingId, kind]);

  const total = steps.length;
  const doneCount = useMemo(() => {
    return steps.filter((s) => stepAnswered(s, state.items[s.key])).length;
  }, [steps, state.items]);

  if (!enabled && !alreadySubmitted) return null;

  const persist = (next) => {
    setState(next);
    saveDraft(bookingId, kind, next);
  };

  const cursor = Math.min(state.cursor ?? 0, total - 1);
  const current = steps[cursor];

  const T = lang === "no" ? {
    step: "Steg", of: "av",
    checkin_title: "Selv-innsjekk",
    checkin_sub: "Fullfør sjekklisten for å registrere at du har hentet båten på Porsgrunn gjestehavn.",
    checkout_title: "Selv-utsjekk",
    checkout_sub: "Avslutt turen – gå gjennom sjekklisten før du forlater båten.",
    start: "Start selvsjekk",
    resume: "Fortsett",
    reset: "Start på nytt",
    prev: "Forrige",
    next: "OK – neste",
    yes: "Ja", no_: "Nei",
    number_placeholder: "Antall",
    text_placeholder_no: "Skriv her (valgfritt)",
    submit: "Send inn selv-sjekk",
    submitting: "Sender…",
    submitted_title: "Selvsjekk levert – takk!",
    submitted_body: (dt) => `Levert ${new Date(dt).toLocaleString("nb-NO")}.`,
    optional: "Valgfritt",
    photo_add_note: "Legg til beskrivelse eller bilde – begge deler er valgfritt.",
    not_started: "Ikke startet",
    remaining: (n) => `${n} steg gjenstår`,
    all_answered: "Alle steg besvart",
  } : {
    step: "Step", of: "of",
    checkin_title: "Self check-in",
    checkin_sub: "Complete the checklist to register that you have picked up the boat at Porsgrunn Guest Harbour.",
    checkout_title: "Self check-out",
    checkout_sub: "End the trip – walk through the checklist before leaving the boat.",
    start: "Start self-check",
    resume: "Continue",
    reset: "Start over",
    prev: "Previous",
    next: "OK – next",
    yes: "Yes", no_: "No",
    number_placeholder: "Number",
    text_placeholder_no: "Type here (optional)",
    submit: "Submit self-check",
    submitting: "Sending…",
    submitted_title: "Self-check submitted – thank you!",
    submitted_body: (dt) => `Submitted ${new Date(dt).toLocaleString("en-GB")}.`,
    optional: "Optional",
    photo_add_note: "Add a description or photo – both are optional.",
    not_started: "Not started",
    remaining: (n) => `${n} step${n === 1 ? "" : "s"} left`,
    all_answered: "All steps answered",
  };

  const label = (s) => (lang === "no" ? s.no : s.en);
  const Icon = kind === "checkin" ? LogIn : LogOut;
  const title = kind === "checkin" ? T.checkin_title : T.checkout_title;
  const subtitle = kind === "checkin" ? T.checkin_sub : T.checkout_sub;
  const accent = kind === "checkin" ? "#BD984D" : "#1DA3B3";
  const accentSoft = kind === "checkin" ? "bg-[#BD984D]/10" : "bg-[#1DA3B3]/10";
  const accentBtn = kind === "checkin"
    ? "bg-[#BD984D] hover:bg-[#A57F39] text-white"
    : "bg-[#1DA3B3] hover:bg-[#178791] text-white";

  const setAnswer = (patch) => {
    const cur = state.items[current.key] || {};
    persist({
      ...state,
      items: { ...state.items, [current.key]: { ...cur, ...patch } },
      started: true,
    });
  };

  const goNext = () => {
    const nextIdx = Math.min(cursor + 1, total - 1);
    persist({ ...state, cursor: nextIdx, started: true });
  };
  const goPrev = () => {
    persist({ ...state, cursor: Math.max(cursor - 1, 0), started: true });
  };
  const start = () => {
    setExpanded(true);
    persist({ ...state, started: true });
  };
  const reset = () => persist({ items: {}, cursor: 0, started: false });

  const answer = state.items[current?.key] || {};
  const canAdvance = stepAnswered(current, answer);
  const isLast = cursor === total - 1;
  const allAnswered = doneCount === total;

  const submit = async () => {
    setSubmitting(true);
    try {
      const items = steps.map((s) => {
        const a = state.items[s.key] || {};
        return {
          key: s.key,
          type: s.type,
          value: a.value ?? null,
          text: a.text ?? null,
          photo_ids: a.photo_ids || [],
        };
      });
      await api.post("/self-checks", {
        booking_id: bookingId, kind, items,
      });
      clearDraft(bookingId, kind);
      toast.success(T.submitted_title);
      onSubmitted?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Feil ved innsending");
    } finally {
      setSubmitting(false);
    }
  };

  const progressPct = Math.round((doneCount / total) * 100);

  // ------ already submitted (read-only summary) ------
  if (alreadySubmitted) {
    return (
      <Card className="p-5 sm:p-6 border-emerald-300 bg-emerald-50/40" data-testid={`self-${kind}-submitted`}>
        <div className="flex items-start gap-3">
          <div className="p-2.5 rounded-lg bg-emerald-500 text-white">
            <Check className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-semibold text-[#0C1824]">{T.submitted_title}</h2>
            <p className="text-sm text-[#2B3F4D] mt-1">
              {T.submitted_body(alreadySubmitted.submitted_at)}
            </p>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card
      className="overflow-hidden"
      style={{ borderColor: `${accent}55` }}
      data-testid={`self-${kind}-wizard`}
    >
      {/* ------ header ------ */}
      <button
        type="button"
        className={`w-full text-left p-5 sm:p-6 flex items-start gap-4 hover:${accentSoft} transition-colors`}
        onClick={() => setExpanded((v) => !v)}
        data-testid={`self-${kind}-toggle`}
        aria-expanded={expanded}
      >
        <div className={`p-2.5 rounded-lg ${accentSoft} shrink-0`} style={{ color: accent }}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-xl font-semibold text-[#0C1824]">{title}</h2>
            {!state.started && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-medium">
                {T.not_started}
              </span>
            )}
            {state.started && !allAnswered && (
              <span className="text-xs px-2 py-0.5 rounded-full font-medium"
                    style={{ backgroundColor: `${accent}22`, color: accent }}>
                {T.remaining(total - doneCount)}
              </span>
            )}
            {allAnswered && !alreadySubmitted && (
              <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-emerald-100 text-emerald-700">
                {T.all_answered}
              </span>
            )}
          </div>
          <p className="text-sm text-[#2B3F4D] mt-1">{subtitle}</p>
          <div className="mt-3 h-1.5 w-full rounded-full bg-slate-200 overflow-hidden">
            <div
              className="h-full transition-all"
              style={{ width: `${progressPct}%`, backgroundColor: accent }}
            />
          </div>
          <div className="mt-1.5 text-[11px] font-mono text-[#2B3F4D]">
            {doneCount}/{total}
          </div>
        </div>
        <div className="shrink-0" style={{ color: accent }}>
          {expanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
        </div>
      </button>

      {/* ------ body ------ */}
      {expanded && (
        <div className="px-5 sm:px-6 pb-6" data-testid={`self-${kind}-body`}>
          {!state.started ? (
            <div className="text-center py-6">
              <Button onClick={start} className={accentBtn} data-testid={`self-${kind}-start`}>
                {T.start}
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between text-xs uppercase tracking-widest">
                <span className="font-semibold" style={{ color: accent }}>
                  {T.step} {cursor + 1} {T.of} {total}
                </span>
                <button
                  type="button"
                  onClick={reset}
                  className="text-slate-500 hover:text-red-600 transition inline-flex items-center gap-1"
                  data-testid={`self-${kind}-reset`}
                >
                  <RotateCcw className="w-3 h-3" /> {T.reset}
                </button>
              </div>

              <div
                className="rounded-xl border-2 p-5 sm:p-6"
                style={{ borderColor: `${accent}66`, backgroundColor: `${accent}0f` }}
              >
                <div className="flex items-start gap-4">
                  <div
                    className="h-10 w-10 rounded-full text-white flex items-center justify-center font-bold text-lg shrink-0"
                    style={{ backgroundColor: accent }}
                  >
                    {cursor + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-lg sm:text-xl font-semibold text-[#0C1824] leading-snug">
                      {current.hint_icon === "key" && (
                        <KeyRound className="inline w-5 h-5 mr-2 -mt-1" style={{ color: accent }} />
                      )}
                      {label(current)}
                    </div>
                    {current.optional_note && (
                      <div className="text-xs uppercase tracking-widest mt-1 opacity-70">
                        {T.optional}
                      </div>
                    )}

                    {/* ---------- per-type input ---------- */}
                    <div className="mt-4">
                      {current.type === "check" && (
                        <Button
                          onClick={() => setAnswer({ value: true })}
                          disabled={answer.value === true}
                          className={accentBtn}
                          data-testid={`self-${kind}-check`}
                        >
                          <Check className="w-4 h-4 mr-1.5" />
                          {answer.value === true ? (lang === "no" ? "Bekreftet" : "Confirmed") : (lang === "no" ? "Bekreft" : "Confirm")}
                        </Button>
                      )}

                      {current.type === "yesno" && (
                        <div className="flex gap-2">
                          <Button
                            variant={answer.value === true ? "default" : "outline"}
                            onClick={() => setAnswer({ value: true })}
                            className={answer.value === true ? accentBtn : ""}
                            data-testid={`self-${kind}-yes`}
                          >
                            {T.yes}
                          </Button>
                          <Button
                            variant={answer.value === false ? "default" : "outline"}
                            onClick={() => setAnswer({ value: false })}
                            className={answer.value === false ? "bg-slate-700 hover:bg-slate-800 text-white" : ""}
                            data-testid={`self-${kind}-no`}
                          >
                            {T.no_}
                          </Button>
                        </div>
                      )}

                      {current.type === "count" && (
                        <Input
                          type="number"
                          inputMode="numeric"
                          min={0}
                          max={99}
                          value={typeof answer.value === "number" ? answer.value : ""}
                          onChange={(e) => {
                            const v = e.target.value === "" ? null : parseInt(e.target.value, 10);
                            setAnswer({ value: Number.isFinite(v) ? v : null });
                          }}
                          placeholder={T.number_placeholder}
                          className="max-w-[160px] text-lg"
                          data-testid={`self-${kind}-count`}
                        />
                      )}

                      {current.type === "text" && (
                        <Textarea
                          rows={3}
                          value={answer.text || ""}
                          onChange={(e) => setAnswer({ text: e.target.value })}
                          placeholder={T.text_placeholder_no}
                          data-testid={`self-${kind}-text`}
                        />
                      )}

                      {current.type === "photo_report" && (
                        <div className="space-y-3">
                          <p className="text-xs text-[#2B3F4D]">{T.photo_add_note}</p>
                          <Textarea
                            rows={3}
                            value={answer.text || ""}
                            onChange={(e) => setAnswer({ text: e.target.value })}
                            placeholder={lang === "no" ? "Beskriv skade/observasjon" : "Describe the damage/observation"}
                            data-testid={`self-${kind}-damage-text`}
                          />
                          <PhotoUploader
                            photoIds={answer.photo_ids || []}
                            onChange={(ids) => setAnswer({ photo_ids: ids })}
                            label={lang === "no" ? "Legg til bilder" : "Add photos"}
                          />
                        </div>
                      )}

                      {current.type === "info" && (
                        <div className="flex items-center gap-2 text-[#0C1824] font-medium">
                          <Sparkles className="w-5 h-5" style={{ color: accent }} />
                          <span>{lang === "no" ? "Nesten ferdig – trykk «OK – neste» og deretter «Send inn»." : "Almost done – tap OK – next then Submit."}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between gap-3">
                <Button
                  variant="outline"
                  onClick={goPrev}
                  disabled={cursor === 0}
                  data-testid={`self-${kind}-prev`}
                >
                  <ArrowLeft className="w-4 h-4 mr-1.5" /> {T.prev}
                </Button>
                {!isLast ? (
                  <Button
                    onClick={goNext}
                    disabled={!canAdvance}
                    className={`${accentBtn} min-w-[140px]`}
                    data-testid={`self-${kind}-next`}
                    size="lg"
                  >
                    <Check className="w-4 h-4 mr-1.5" /> {T.next}
                  </Button>
                ) : (
                  <Button
                    onClick={submit}
                    disabled={submitting || !allAnswered}
                    className={`${accentBtn} min-w-[160px]`}
                    data-testid={`self-${kind}-submit`}
                    size="lg"
                  >
                    <Check className="w-4 h-4 mr-1.5" />
                    {submitting ? T.submitting : T.submit}
                  </Button>
                )}
              </div>

              {isLast && !allAnswered && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 flex items-start gap-2">
                  <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>
                    {lang === "no"
                      ? "Alle steg må besvares før du kan sende inn. Bruk «Forrige» for å fylle inn det som mangler."
                      : "All steps must be answered before submission. Use «Previous» to fill in what's missing."}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
